import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Compass, MapPinned, Sparkles, Star, UserRound, UsersRound } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  buildSparkCardData,
  buildSparkRevealItems,
  defaultSparkSyncValues,
  getSparkLocationLabel,
  type SparkForOption,
  type SparkLocationOption,
  type SparkSyncFormValues,
  type SparkSyncField,
  validateSparkSyncStep,
} from "@/lib/sparkSync";

interface SparkSyncGameProps {
  conciergeName?: string | null;
}

type GameStage = 1 | 2 | 3 | 4 | 5;

const sparkForCards: Array<{
  value: SparkForOption;
  title: string;
  description: string;
  icon: typeof UserRound;
}> = [
  {
    value: "specific",
    title: "Specific",
    description: "A single person with a distinct point of view.",
    icon: UserRound,
  },
  {
    value: "orbit",
    title: "Orbit",
    description: "Someone in your wider household orbit or gifting circle.",
    icon: UsersRound,
  },
  {
    value: "myself",
    title: "Myself",
    description: "A Spark for your own next well-deserved indulgence.",
    icon: Sparkles,
  },
];

const locationCards: Array<{
  value: SparkLocationOption;
  title: string;
  description: string;
}> = [
  {
    value: "port-macquarie",
    title: "Port Macquarie",
    description: "Local-first options with a polished coastal lens.",
  },
  {
    value: "coastal-nsw",
    title: "Coastal NSW",
    description: "A wider shoreline edit for refined regional gifting.",
  },
  {
    value: "forster",
    title: "Forster",
    description: "Lakeside charm with a relaxed coastal character.",
  },
  {
    value: "tuncurry",
    title: "Tuncurry",
    description: "Quiet waterfront elegance for considered gifting.",
  },
];

export const SparkSyncGame = ({ conciergeName }: SparkSyncGameProps) => {
  const [stage, setStage] = useState<GameStage>(1);
  const [values, setValues] = useState<SparkSyncFormValues>(defaultSparkSyncValues);
  const [errors, setErrors] = useState<Partial<Record<SparkSyncField, string>>>({});
  const [revealedCount, setRevealedCount] = useState(0);

  const revealItems = useMemo(() => buildSparkRevealItems(values), [values]);
  const sparkCard = useMemo(() => buildSparkCardData(values), [values]);
  const assistantName = conciergeName ?? "Rebecca";

  useEffect(() => {
    if (stage !== 5) {
      return;
    }

    setRevealedCount(0);
    const timers = revealItems.map((_, index) =>
      window.setTimeout(() => {
        setRevealedCount(index + 1);
      }, 450 * (index + 1)),
    );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [revealItems, stage]);

  const setFieldValue = <K extends SparkSyncField>(field: K, value: SparkSyncFormValues[K]) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleNext = () => {
    const nextErrors = validateSparkSyncStep(stage as 1 | 2 | 3 | 4, values);

    if (Object.keys(nextErrors).length > 0) {
      setErrors((current) => ({ ...current, ...nextErrors }));
      return;
    }

    setErrors({});
    setStage((current) => (current < 5 ? ((current + 1) as GameStage) : current));
  };

  const handleReset = () => {
    setValues(defaultSparkSyncValues);
    setErrors({});
    setRevealedCount(0);
    setStage(1);
  };

  const stepLabel =
    stage === 1
      ? "Who is this Spark for?"
      : stage === 2
        ? "Logistics"
        : stage === 3
          ? "Location"
          : stage === 4
            ? "The why"
            : "Spark reveal";

  return (
    <Card className="overflow-hidden border-primary/20 bg-card/90 shadow-xl shadow-background/25">
      <CardContent className="space-y-5 p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Spark-Sync</p>
            <h3 className="mt-1 text-lg font-semibold text-foreground">{stepLabel}</h3>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              {assistantName} will shape this Spark with elegant guardrails and concierge-level restraint.
            </p>
          </div>
          <div className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            Step {stage} of 5
          </div>
        </div>

        <div className="grid grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5].map((item) => (
            <div
              key={item}
              className={`h-1.5 rounded-full ${item <= stage ? "bg-primary" : "bg-muted"}`}
            />
          ))}
        </div>

        {stage === 1 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Who is this Spark for?</p>
            <div className="grid gap-3 sm:grid-cols-3">
              {sparkForCards.map(({ value, title, description, icon: Icon }) => {
                const selected = values.sparkFor === value;

                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFieldValue("sparkFor", value)}
                    className={`rounded-2xl border p-4 text-left transition-all ${
                      selected
                        ? "border-primary bg-primary/10 shadow-md shadow-primary/10"
                        : "border-border bg-background hover:border-primary/40 hover:bg-accent/50"
                    }`}
                  >
                    <Icon className="h-5 w-5 text-primary" />
                    <h4 className="mt-3 text-sm font-semibold text-foreground">{title}</h4>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
                  </button>
                );
              })}
            </div>
            {errors.sparkFor && <p className="text-sm font-medium text-destructive">{errors.sparkFor}</p>}
          </div>
        )}

        {stage === 2 && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Age</label>
                <Input
                  value={values.age}
                  onChange={(event) => setFieldValue("age", event.target.value)}
                  placeholder="For example: 34"
                />
                {errors.age && <p className="text-sm font-medium text-destructive">{errors.age}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Gender</label>
                <Input
                  value={values.gender}
                  onChange={(event) => setFieldValue("gender", event.target.value)}
                  placeholder="For example: woman, man, non-binary"
                />
                {errors.gender && <p className="text-sm font-medium text-destructive">{errors.gender}</p>}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Likes</label>
              <Textarea
                value={values.likes}
                onChange={(event) => setFieldValue("likes", event.target.value)}
                placeholder="List the tastes, rituals, hobbies, textures, or brands that genuinely suit them."
                className="min-h-28"
              />
              {errors.likes && <p className="text-sm font-medium text-destructive">{errors.likes}</p>}
            </div>
          </div>
        )}

        {stage === 3 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPinned className="h-4 w-4 text-primary" />
              Choose your Coastal NSW gifting destination.
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {locationCards.map(({ value, title, description }) => {
                const selected = values.location === value;

                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFieldValue("location", value)}
                    className={`rounded-2xl border p-4 text-left transition-all ${
                      selected
                        ? "border-primary bg-primary/10 shadow-md shadow-primary/10"
                        : "border-border bg-background hover:border-primary/40 hover:bg-accent/50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="text-sm font-semibold text-foreground">{title}</h4>
                      <Compass className="h-4 w-4 text-primary" />
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{description}</p>
                  </button>
                );
              })}
            </div>
            {errors.location && <p className="text-sm font-medium text-destructive">{errors.location}</p>}
          </div>
        )}

        {stage === 4 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Star className="h-4 w-4 text-primary" />
              This field is mandatory. Rebecca will shape the Spark Card directly from your why.
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Why does this Spark matter?</label>
              <Textarea
                value={values.why}
                onChange={(event) => setFieldValue("why", event.target.value)}
                placeholder="Tell Rebecca what they deserve to feel, celebrate, or remember when this gift arrives."
                className="min-h-36"
              />
              {errors.why && <p className="text-sm font-medium text-destructive">{errors.why}</p>}
            </div>
          </div>
        )}

        {stage === 5 && (
          <div className="space-y-5">
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
              <p className="text-sm leading-relaxed text-muted-foreground">
                Five boxes, each opened with intention: two Sale discoveries, one Popular favourite, one Rebecca’s choice, and one wildcard with charm.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {revealItems.map((item, index) => {
                const isOpen = index < revealedCount;

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 18, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: index * 0.08, duration: 0.32 }}
                    className="relative overflow-hidden rounded-2xl border border-border bg-card"
                  >
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,hsl(var(--secondary)/0.22),transparent_55%)]" />
                    <motion.div
                      animate={isOpen ? { rotateX: -72, y: -20 } : { rotateX: 0, y: 0 }}
                      transition={{ type: "spring", stiffness: 160, damping: 18 }}
                      className="absolute inset-x-3 top-3 h-12 origin-top rounded-xl border border-secondary/30 bg-secondary/20"
                    />
                    <div className="relative flex min-h-48 flex-col justify-end p-4 pt-16">
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
                          {item.category}
                        </span>
                        {!isOpen && (
                          <span className="rounded-full border border-secondary/20 bg-secondary/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-secondary-foreground">
                            Sealed
                          </span>
                        )}
                      </div>

                      {isOpen ? (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-2"
                        >
                          <h4 className="text-base font-semibold text-foreground">{item.title}</h4>
                          <p className="text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                        </motion.div>
                      ) : (
                        <div className="space-y-2">
                          <div className="h-4 w-2/3 rounded-full bg-muted" />
                          <div className="h-3 w-full rounded-full bg-muted/80" />
                          <div className="h-3 w-4/5 rounded-full bg-muted/70" />
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {revealedCount === revealItems.length && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                <div className="overflow-hidden rounded-[1.75rem] border border-primary/20 bg-[linear-gradient(160deg,hsl(var(--background)),hsl(var(--card)),hsl(var(--secondary)/0.14))] p-5 shadow-xl shadow-background/25">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Digital Spark card</p>
                      <h4 className="mt-2 text-xl font-semibold text-foreground">{sparkCard.title}</h4>
                    </div>
                    <Sparkles className="h-5 w-5 text-primary" />
                  </div>
                  <div className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
                    <p>{sparkCard.audienceLine}</p>
                    <p>{sparkCard.locationLine}</p>
                    <p className="text-foreground">{sparkCard.reasonLine}</p>
                    <p>{sparkCard.conciergeNote}</p>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {sparkCard.keywords.map((keyword) => (
                      <span
                        key={keyword}
                        className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button type="button" variant="ghost" onClick={handleReset}>
            Start over
          </Button>
          <div className="flex items-center gap-2">
            {stage > 1 && stage < 5 && (
              <Button type="button" variant="outline" onClick={() => setStage((current) => ((current - 1) as GameStage))}>
                Back
              </Button>
            )}
            {stage < 5 ? (
              <Button type="button" variant="secondary" onClick={handleNext}>
                Continue
              </Button>
            ) : (
              <Button type="button" variant="secondary" onClick={handleReset}>
                Compose another Spark
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};